import React, { useEffect, useMemo, useState } from 'react';
import { useNode } from '@craftjs/core';
import { Button } from '@/components/ui/button';
import { VehicleCard } from './VehicleCard';
import Marquee from 'react-fast-marquee';
import { Vehicle } from '@/utils/types';
import useClientStore from '@/store/useClientStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { useEnsureVehicles } from '@/hooks/useEnsureVehicles';
import { normalizeBuilderLink } from '@/utils/functions';
import { DEFAULT_RECENCY_DAYS, isHistoricalImport, isWithinWindow, latestEventDate } from '@/utils/webVisibility';
import { htmlSeguro } from '../../utils/htmlSeguro';
import { compararFechaDelTenant, compararPrioridad, ordenDelTenant } from '@/lib/orden-vehiculos';
import { precioModoDelCliente } from '@/lib/precio-web';

// Versión simplificada del vehículo - EXPORT this interface
export interface SimpleVehicle extends Vehicle {
  label?: string; // Campo para etiqueta personalizada
  vehicles_sales?:
    | Array<{ created_at: string; [key: string]: any }>
    | { created_at: string; [key: string]: any }
    | null;
  vehicles_reservations?:
    | Array<{ created_at: string; [key: string]: any }>
    | { created_at: string; [key: string]: any }
    | null;
  event_date?: string; // To store the created_at of the sale/reservation event
}

interface VehicleCarouselProps {
  title?: string;
  subtitle?: string;
  bgColor?: string;
  textColor?: string;
  buttonText?: string;
  buttonLink?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  autoplay?: boolean;
  autoplaySpeed?: number;
  itemsToShow?: number;
  // 'available' = vehículos disponibles (default).
  // 'sold' = recién vendidos, ordenados por fecha de venta desc.
  mode?: 'available' | 'sold';
  showStatuses?: ('Publicado' | 'Vendido' | 'Reservado')[];
  cardSettings?: {
    cardBgColor: string;
    cardBorderColor: string;
    cardTextColor: string;
    cardPriceColor: string;
    cardButtonColor: string;
    cardButtonTextColor: string;
    detailsButtonText: string;
    bannerPosition: 'left' | 'right';
    pricePosition: 'overlay' | 'below-title';
    featuresConfig?: {
      feature1: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
      feature2: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
      feature3: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
      feature4: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
    };
  }[];
  newBadgeText?: string; // New prop for the "Recién publicado" badge text
  children?: React.ReactNode;
}

export const VehicleCarousel = ({
  title = 'Vehículos destacados',
  subtitle = 'Descubre nuestra selección de vehículos',
  bgColor = '#ffffff',
  textColor = '#333333',
  buttonText = 'Ver todos',
  buttonLink = '/vehicles',
  buttonBgColor = '#3b82f6',
  buttonTextColor = '#ffffff',
  autoplay = true,
  autoplaySpeed = 5000,
  itemsToShow = 3,
  mode = 'available',
  showStatuses = ['Publicado', 'Vendido', 'Reservado'],
  cardSettings = [
    {
      cardBgColor: '#ffffff',
      cardBorderColor: '#e5e7eb',
      cardTextColor: '#1f2937',
      cardPriceColor: '#ffffff',
      cardButtonColor: '#3b82f6',
      cardButtonTextColor: '#ffffff',
      detailsButtonText: 'Ver detalles',
      bannerPosition: 'right',
      pricePosition: 'overlay',
      featuresConfig: {
        feature1: 'category',
        feature2: 'year',
        feature3: 'fuel',
        feature4: 'mileage',
      },
    },
  ],
  newBadgeText = 'Nuevo',
  children,
}: VehicleCarouselProps) => {
  const {
    connectors: { connect },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  // Convert string-based props to their actual types
  const autoplayValue =
    typeof autoplay === 'string' ? autoplay === 'true' : autoplay;
  const itemsToShowValue =
    typeof itemsToShow === 'string' ? parseInt(itemsToShow) : itemsToShow;

  // Handle complex showStatuses — memoize to avoid infinite re-renders
  const statusValues = useMemo(() => {
    if (
      Array.isArray(showStatuses) &&
      showStatuses.length > 0 &&
      typeof showStatuses[0] === 'object'
    ) {
      return showStatuses.map((item: any) => item.status);
    }
    return showStatuses;
  }, [showStatuses]);

  const { client } = useClientStore();
  // Ticket #164: orden inicial que configuró la automotora.
  const ordenTenant = ordenDelTenant((client as Record<string, unknown> | null)?.web_vehicle_order);

  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState<SimpleVehicle[]>([]);
  const [loading, setLoading] = useState(true);

  // Ajustar cantidad de vehículos visibles según el tamaño de pantalla
  const getVisibleItems = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 1;
      if (window.innerWidth < 1024) return Math.min(2, itemsToShowValue);
      return itemsToShowValue;
    }
    return itemsToShowValue;
  };

  const [visibleItems, setVisibleItems] = useState(getVisibleItems());

  useEffect(() => {
    const handleResize = () => {
      setVisibleItems(getVisibleItems());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Inventario del tenant, compartido con el resto del sitio (ver
  // useVehiclesStore): una sola descarga por visita; acá se le aplican las
  // reglas propias del carrusel, que no cambian.
  const {
    rawVehicles: inventario,
    isLoading: inventarioCargando,
    loadedForClientId,
  } = useEnsureVehicles();
  const inventarioListo = !inventarioCargando && String(loadedForClientId) === String(client?.id);

  useEffect(() => {
    const fetchVehicles = () => {
      if (!client?.id) return;
      try {
        {
          const data = inventario.filter((v) => String(v.client_id) === String(client.id));
          // Fecha del evento (venta/reserva con fallback a state_updated_at /
          // updated_at, para que un cambio de estado sin registro formal no
          // esconda el vehículo de inmediato)
          const processedData = data.map((vehicle: any) => ({
            ...vehicle,
            event_date: latestEventDate(vehicle),
          }));

          const vehiclesData = processedData as unknown as SimpleVehicle[];

          // Filtrar vehículos por estado y luego por fecha de evento para Vendido/Reservado
          console.log('vehicles data with event_date', vehiclesData);
          console.log('status values', statusValues);

          // Per-state visibility window (utils/webVisibility.ts): NULL/<=0 =
          // always show; positivo = solo si event_date está dentro de N días.

          // Filter vehicles según mode:
          // - mode='sold': solo vendidos, respetando la ventana del estado.
          // - mode='available': comportamiento histórico (estados visibles + ventana propia).
          const activelyFilteredVehicles = vehiclesData.filter((vehicle) => {
            if (!vehicle.status) return false;
            // La historia importada no se publica nunca. Va antes que todo lo
            // demás porque acá isWithinWindow recibe web_visibility_days CRUDO y
            // devuelve true cuando es NULL: sin esta salida un vendido histórico
            // quedaría visible para siempre en los tenants que nunca configuraron
            // la ventana, que son casi todos.
            if (isHistoricalImport(vehicle)) return false;
            const stateDays = vehicle.status.web_visibility_days;
            const isSold = vehicle.status.name === 'Vendido';

            if (mode === 'sold') {
              if (!isSold) return false;
              return isWithinWindow(vehicle.event_date, stateDays);
            }

            // Determine if vehicle should be shown based on show_in_web or fallback to names
            let shouldShow = false;
            if (typeof vehicle.status.show_in_web === 'boolean') {
              shouldShow = vehicle.status.show_in_web;
            } else {
              shouldShow = statusValues.includes(vehicle.status.name as any);
            }

            if (!shouldShow) return false;

            // Un Vendido/Reservado SIN ventana configurada no puede quedar
            // visible para siempre: es lo que hacía que "recién llegados"
            // mostrara todos los vendidos del tenant. Casi ninguno configura
            // `web_visibility_days`, y con NULL `isWithinWindow` devuelve true.
            // Se cae al mismo default que ya usan las demás superficies a
            // través de `passesRecencyWindow`, sin inventar un número propio.
            //
            // Solo acá (mode='available'): `mode='sold'` conserva NULL = sin
            // ventana, que es de lo que dependen los carruseles de vendidos.
            const llevaVentana = isSold || vehicle.status.name === 'Reservado';
            const dias = llevaVentana ? (stateDays ?? DEFAULT_RECENCY_DAYS) : stateDays;
            return isWithinWindow(vehicle.event_date, dias);
          });

          // mode='sold': ordenar por event_date (fecha de venta) desc
          if (mode === 'sold') {
            const sortedSold = [...activelyFilteredVehicles].sort((a, b) => {
              const dateA = a.event_date ? new Date(a.event_date).getTime() : 0;
              const dateB = b.event_date ? new Date(b.event_date).getTime() : 0;
              return dateB - dateA;
            });
            setVehicles(sortedSold);
            return;
          }

          // Ordenar para que los vehículos con estado "Publicado" aparezcan primero y luego por precio
          const sortedVehicles = [...activelyFilteredVehicles].sort((a, b) => {
            // Prioritize "Publicado" status
            if (
              a.status?.name === 'Publicado' &&
              b.status?.name !== 'Publicado'
            ) {
              return -1;
            }
            if (
              a.status?.name !== 'Publicado' &&
              b.status?.name === 'Publicado'
            ) {
              return 1;
            }

            // Ticket #164: entre autos del mismo "nivel" de estado manda el
            // orden que configuró la automotora. Va DESPUÉS de la regla de
            // Publicado a propósito: el carrusel muestra primero lo que se
            // puede comprar y recién dentro de eso ordena por fecha. Con el
            // default ('newest') el resultado es idéntico al de siempre.
            //
            // Primero la PRIORIDAD del vehículo (Muy alta → Alta → Normal →
            // Baja) y después la fecha en el sentido del tenant. Con todo en
            // Normal —el estado de hoy— la prioridad no decide nada.
            const porPrioridad = compararPrioridad(a, b);
            if (porPrioridad !== 0) return porPrioridad;

            // Sólo la FECHA: el empate lo sigue resolviendo el precio de abajo,
            // que es la regla propia del carrusel y es anterior a este ticket.
            // Con el desempate por `id` metido acá, esa regla quedaba muerta —
            // invisible en casi todas las automotoras, pero no en las que
            // cargaron su stock de una vez y tienen todo el mismo created_at.
            if (a.created_at && b.created_at) {
              const porFecha = compararFechaDelTenant(ordenTenant)(a, b);
              if (porFecha !== 0) return porFecha;
            }

            // If created_at is also the same (or one is missing), then sort by price
            const tempPriceA =
              a.price && a.discount_percentage && a.discount_percentage > 0
                ? a.price * (1 - a.discount_percentage / 100)
                : a.price;
            const tempPriceB =
              b.price && b.discount_percentage && b.discount_percentage > 0
                ? b.price * (1 - b.discount_percentage / 100)
                : b.price;

            const priceA = tempPriceA ?? Infinity;
            const priceB = tempPriceB ?? Infinity;

            if (priceA < priceB) {
              return -1;
            }
            if (priceA > priceB) {
              return 1;
            }

            return 0;
          });

          console.log('filtered by status, date, and price', sortedVehicles);

          setVehicles(sortedVehicles);
        }
      } catch (err) {
        console.error('Failed to fetch vehicles:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!client?.id) return;
    if (!inventarioListo) {
      setLoading(true);
      return;
    }
    fetchVehicles();
    // `ordenTenant` explícito: el efecto ya depende de `client?.id`, pero el
    // orden sale de OTRO campo de la misma fila y no hay garantía de que los
    // dos lleguen en el mismo render.
  }, [statusValues, client?.id, inventario, inventarioListo, ordenTenant]);

  return (
    <div
      ref={(ref: HTMLDivElement | null) => { if (ref) connect(ref); }}
      style={{
        background: bgColor,
        color: textColor,
        padding: '40px 0',
        position: 'relative',
        border: selected ? '1px dashed #1e88e5' : '1px solid transparent',
        minWidth: 0,
      }}
      className='w-full VehicleCarousel'
      data-section='vehicle-carousel'
    >
      <div className='max-w-7xl mx-auto px-4 sm:px-6 overflow-hidden'>
        {/* Encabezado */}
        <div className='flex justify-between items-center mb-8'>
          <div>
            <h2
              style={{ color: textColor }}
              className='text-3xl font-bold mb-2'
            >
              {title}
            </h2>
            <p style={{ color: textColor }} className='text-base text-gray-600'>
              {subtitle}
            </p>
          </div>
          <div>
            <Button
              style={{
                backgroundColor: buttonBgColor,
                color: buttonTextColor,
                borderColor: 'transparent',
              }}
              className='hover:opacity-90 transition-opacity'
              asChild
            >
              <a href={normalizeBuilderLink(buttonLink)} dangerouslySetInnerHTML={htmlSeguro(buttonText || '')} />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className='flex justify-center items-center h-60'>
            <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500'></div>
          </div>
        ) : vehicles.length === 0 ? (
          <div className='text-center p-8 bg-gray-50 rounded-xl'>
            <h3 className='text-lg font-medium text-gray-700 mb-2'>
              {t('site.vehicles.noneAvailableTitle')}
            </h3>
            <p className='text-gray-500 mb-4'>
              {t('site.vehicles.noneAvailableText')}
            </p>
          </div>
        ) : (
          <div className='w-full overflow-hidden'>
            <Marquee
              gradient={false}
              speed={autoplayValue ? 50 : 0}
              pauseOnHover={true}
            >
              {vehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className='px-2'
                  style={{
                    width: '320px',
                  }}
                >
                  <VehicleCard
                    vehicle={vehicle}
                    compact={false}
                    precioModo={precioModoDelCliente(client)}
                    cardBgColor={cardSettings[0]?.cardBgColor}
                    cardBorderColor={cardSettings[0]?.cardBorderColor}
                    cardTextColor={cardSettings[0]?.cardTextColor}
                    cardPriceColor={cardSettings[0]?.cardPriceColor}
                    cardButtonColor={cardSettings[0]?.cardButtonColor}
                    cardButtonTextColor={cardSettings[0]?.cardButtonTextColor}
                    detailsButtonText={cardSettings[0]?.detailsButtonText}
                    bannerPosition={cardSettings[0]?.bannerPosition}
                    pricePosition={cardSettings[0]?.pricePosition}
                    featuresConfig={cardSettings[0]?.featuresConfig}
                    newBadgeText={newBadgeText}
                  />
                </div>
              ))}
            </Marquee>
          </div>
        )}

        {children}
      </div>
    </div>
  );
};

VehicleCarousel.craft = {
  displayName: 'VehicleCarousel',
  props: {
    title: 'Vehículos destacados',
    subtitle: 'Descubre nuestra selección de vehículos',
    bgColor: '#ffffff',
    textColor: '#333333',
    buttonText: 'Ver todos',
    buttonLink: '/vehicles',
    buttonBgColor: '#3b82f6',
    buttonTextColor: '#ffffff',
    autoplay: 'true',
    autoplaySpeed: 50,
    itemsToShow: '3',
    mode: 'available',
    showStatuses: [{ status: 'Publicado' }],
    cardSettings: [
      {
        cardBgColor: '#ffffff',
        cardBorderColor: '#e5e7eb',
        cardTextColor: '#1f2937',
        cardPriceColor: '#ffffff',
        cardButtonColor: '#3b82f6',
        cardButtonTextColor: '#ffffff',
        detailsButtonText: 'Ver detalles',
        bannerPosition: 'right',
        pricePosition: 'overlay',
        featuresConfig: {
          feature1: 'category',
          feature2: 'year',
          feature3: 'fuel',
          feature4: 'mileage',
        },
      },
    ],
    newBadgeText: 'Nuevo',
  },
  related: {
    // Settings component will be external
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true,
    canDelete: () => true,
  },
  isCanvas: true,
};
